import React, { useEffect, useState, useRef } from 'react'
import { safeLocalStorage } from 'helpers/localStorage'
import { Link, useLocation, useMatch, useNavigate, generatePath, useSearchParams } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import { useProject } from 'hooks/useProject'
import { useHubsData } from 'providers/hubs'
import { SIGN_IN, HUB_BASE, HOME, NO_PERMISSION, HUB } from 'routes'
import UserMenu from 'components/sidebar/components/usermenu'
import Search from 'components/search'
import Folders from 'components/sidebar/components/folders'
import SiteHeading, { SiteHubs, SitesLabel } from 'components/sidebar/components/siteHeading'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { useApplicationStore } from 'hooks/application'
import {
  SearchLine as SearchIcon,
  Copy,
  Plus,
  BellLine,
  WhatsNew,
  PanelLeftLine,
  FilledStar,
  NonFilledStar,
  Community,
  Archive,
  Projects
} from 'components/icons'
import NotificationsPanel from 'components/notifications'
import { useNotifications } from 'hooks/notifications'
import WhatsNewPanel from 'components/whatsNew'
import { useWhatsNew } from 'hooks/whatsNew'
import { IContextMenuState, IProject } from 'types'
import {
  APP_URL,
  DEFAULT_AVATAR_PATH,
  DEFAULT_SIDEBAR_WIDTH,
  INITIAL_CONTEXT_MENU_STATE,
  PROJECT,
  SIDEBAR_COLLAPSE_WIDTH,
  SIDEBAR_COLLAPSED_SITES_KEY,
  SIDEBAR_EXPANDED_WIDTH_KEY
} from 'app-constants'
import InviteDialog from 'views/settings/components/inviteDialog'
import Templates from 'components/templates'
import CreateHubModal from 'views/home/components/createModal'
import { capitaliseFirstLetter, validateSourceUrl } from 'helpers/utils'
import ErrorBoundary from 'components/error'
import { COMMUNITY_PROJECT_ID } from 'views/home/constants'
import HubBadge from 'components/sidebar/hubBadge'
import { rowButtonProps } from 'components/sidebar/rowButton'
import { useHubCreation, HUB_CREATION_RESTRICTED_MESSAGE } from 'hooks/useHubCreation'
import { isFavourite, toggleFavouriteHub } from 'helpers/favourites'
import api from 'helpers/api'
import { twMerge } from 'tailwind-merge'
import {
  SIDEBAR_SURFACE,
  SIDEBAR_BORDER,
  SIDEBAR_ROW,
  SIDEBAR_ROW_PAD,
  SIDEBAR_ROW_IDLE,
  SIDEBAR_ROW_ACTIVE,
  SIDEBAR_SCROLL_HIDDEN,
  SIDEBAR_ICON,
  SIDEBAR_ICON_SEMI,
  SIDEBAR_ICON_SIZE,
  SIDEBAR_HERO_BADGE,
  SIDEBAR_SLOT,
  SIDEBAR_ROW_MUTED,
  SIDEBAR_TOP_ROW_HEIGHT,
  SIDEBAR_RESTORE_INSET
} from 'components/sidebar/surface'

/**
 * Opt-in escape hatch for hubs that want the old brand-coloured sidebar.
 *
 * The surface is a neutral light grey by default; a hub's brand colour now
 * reads as an accent (the hub's initial square and the active-row marker)
 * rather than as the whole panel. Making this a real per-hub preference needs
 * a column on the hub model, a migration and a control in Settings > Styling —
 * until that exists this stays off, and the branded code path is kept here so
 * the switch is a one-line change rather than a rewrite.
 */
const USE_HUB_COLOUR_SURFACE = false

const SURFACE = SIDEBAR_SURFACE
const BORDER = SIDEBAR_BORDER

// Same geometry as the resource tree, from the shared tokens, so a hub row and
// a document row inside it line up exactly.
const ROW = `${SIDEBAR_ROW} ${SIDEBAR_ROW_PAD}`
const ROW_IDLE = SIDEBAR_ROW_IDLE
const ROW_ACTIVE = SIDEBAR_ROW_ACTIVE
// A group's name over its rows: an organisation, Starred hubs, Community. Starts in the icon
// column (pl-3.5: the row's 8px and the icon slot's 6px), with the sites and hub badges below it.
const SECTION_LABEL = 'pl-3.5 pr-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted truncate'
const ICON = SIDEBAR_ICON
// A small bordered icon button for the utilities that live at the panel's
// edges (What's new by the logo, Notifications by the user). Its right edge
// lands where the hub header's gear does, 16px in from the panel edge.
// Unfilled, with the glyph at the sidebar icons' strength: these are
// secondary, so they sit on the panel rather than on a white chip.
const EDGE_BUTTON =
  'relative h-7 w-7 p-0 shrink-0 rounded-md bg-transparent hover:bg-sidebar-hover [&>svg]:w-4 [&>svg]:h-4 [&>svg]:opacity-70 hover:[&>svg]:opacity-100 [&_path[stroke]]:[stroke-width:2.4]'
// Matches the two-line block beside it: 16px name line + 12px organisation line.
const USER_AVATAR_SIZE = 28

interface SiteSection {
  key: string
  label: string
  /** The organisation the site belongs to, named on its row when the list mixes organisations. */
  organisation: { key: string; name: string }
  hubs: IProject[]
}

// A to Z, with "Phase 2" before "Phase 10".
const byName = (a: IProject, b: IProject): number => a.name.localeCompare(b.name, undefined, { numeric: true })

// Organisations group hubs into Sites (Organisation → Sites). Each Site gets its
// own section, in its organisation's; hubs outside any Site are returned as unassigned.
// Sites come A to Z by organisation, then by name.
export const groupHubsBySite = (
  hubs: IProject[],
  favourites?: Partial<IProject>[]
): { sites: SiteSection[]; unassigned: IProject[] } => {
  const sections = new Map<string, SiteSection>()
  const unassigned: IProject[] = []

  hubs.forEach((hub) => {
    const site = hub.site
    if (!site?.publicId) {
      unassigned.push(hub)
      return
    }
    if (!sections.has(site.publicId)) {
      const organisation = { key: site.organisation?.publicId ?? '', name: site.organisation?.name ?? '' }
      sections.set(site.publicId, { key: site.publicId, label: site.name, organisation, hubs: [] })
    }
    sections.get(site.publicId)!.hubs.push(hub)
  })

  // Starred hubs lead each list, then each group reads A to Z.
  const starred = (hub: IProject) => (isFavourite(hub.publicId, favourites) ? 0 : 1)
  const starredThenByName = (a: IProject, b: IProject) => starred(a) - starred(b) || byName(a, b)
  sections.forEach((section) => section.hubs.sort(starredThenByName))
  unassigned.sort(starredThenByName)
  const sites = Array.from(sections.values()).sort(
    (a, b) => a.organisation.name.localeCompare(b.organisation.name) || a.label.localeCompare(b.label)
  )
  return { sites, unassigned }
}

// The sites folded away, as stored. Anything unreadable counts as nothing
// folded, so a bad value can only ever show more hubs, never hide them.
const readCollapsedSites = (): Set<string> => {
  try {
    const stored = JSON.parse(safeLocalStorage.getItem(SIDEBAR_COLLAPSED_SITES_KEY) || '[]')
    return new Set(Array.isArray(stored) ? stored : [])
  } catch {
    return new Set()
  }
}

// Opens a panel just outside the sidebar, level with the button that opened
// it. Anchored below the button instead, it would cover the button and the
// sidebar's edge; the menu shifts itself up if it would run off the bottom.
const menuStateBeside = (element: HTMLElement, sidebarWidth: number): IContextMenuState => {
  const { top } = element.getBoundingClientRect()
  return { open: true, top: `${top}px`, left: `${sidebarWidth + 8}px`, right: 'auto', bottom: 'auto' }
}

const AppSidebarContent: React.FC = () => {
  const { user, updateUser } = useAuth()
  const { project, search, openSearch, closeSearch } = useProject()
  const { projects } = useHubsData()
  const { setSnackbarMessage, templatesModal, setTemplatesModal, displayErrorMessage } = useApplicationStore()
  const { sidebarWidth, minSidebarWidth, maxSidebarWidth, setSidebarWidth } = useApplicationStore()
  const dragging = useRef<boolean>(false)
  const mouseStart = useRef<number>(0)
  const originalWidth = useRef<number>(sidebarWidth)
  // The width to come back to after Hide sidebar, so a sidebar you had dragged
  // wider reopens at that width rather than the default. It's saved when the
  // sidebar closes, so a sidebar hidden before a reload still reopens at it.
  const expandedWidth = useRef<number>(
    sidebarWidth || Number(safeLocalStorage.getItem(SIDEBAR_EXPANDED_WIDTH_KEY)) || DEFAULT_SIDEBAR_WIDTH
  )
  const rememberExpandedWidth = (width: number) => {
    expandedWidth.current = width
    safeLocalStorage.setItem(SIDEBAR_EXPANDED_WIDTH_KEY, String(width))
  }
  const collapseSidebar = () => {
    rememberExpandedWidth(sidebarWidth || DEFAULT_SIDEBAR_WIDTH)
    setSidebarWidth(0)
  }
  const location = useLocation()

  const view = location.pathname
  const [userMenu, setUserMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [notificationsMenu, setNotificationsMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [whatsNewMenu, setWhatsNewMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const { hasUnread: hasUnreadWhatsNew } = useWhatsNew()
  const [inviteUserModal, setInviteUserModal] = useState<boolean>(false)
  const [createHubModal, setCreateHubModal] = useState<boolean>(false)
  const [createHubSiteId, setCreateHubSiteId] = useState<string | undefined>(undefined)
  // A site section for another company's hub the user was invited to gets no
  // New hub row, since they cannot add hubs there. Sites of their own organisation
  // that only Site Admins can add hubs to get a disabled one, saying why.
  const { creatableSiteIds, restrictedSiteIds, canCreateHub, restrictionMessage } = useHubCreation()
  // With 100+ hubs across many sites, folding away the sites you don't work in
  // is how the list stays short. Remembered, since those are the same sites
  // every visit.
  const [collapsedSites, setCollapsedSites] = useState<Set<string>>(readCollapsedSites)
  const toggleSite = (siteId: string) => {
    const next = new Set(collapsedSites)
    if (next.has(siteId)) next.delete(siteId)
    else next.add(siteId)
    saveCollapsedSites(next)
  }
  const saveCollapsedSites = (next: Set<string>) => {
    setCollapsedSites(next)
    safeLocalStorage.setItem(SIDEBAR_COLLAPSED_SITES_KEY, JSON.stringify(Array.from(next)))
  }
  // Collapse all sites (the Sites label, or a site's ⋯) folds every site on screen, keeping any others
  // folded too; once they are all folded, the same control is Expand all sites and unfolds them.
  const toggleAllSites = (siteIds: string[]) => {
    const allFolded = siteIds.every((id) => collapsedSites.has(id))
    const next = new Set(collapsedSites)
    siteIds.forEach((id) => (allFolded ? next.delete(id) : next.add(id)))
    saveCollapsedSites(next)
  }
  const { unreadCount } = useNotifications()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const userMenuWidth = 275

  const handleMouseDown = (event: React.MouseEvent) => {
    dragging.current = true
    originalWidth.current = sidebarWidth
    mouseStart.current = event.clientX
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (dragging.current === true) {
      const delta = event.clientX - mouseStart.current
      const newWidth = originalWidth.current + delta
      if (newWidth < SIDEBAR_COLLAPSE_WIDTH) {
        // Dragged well past the minimum: close it, as Hide sidebar does, to reopen at the width it
        // had before this drag. Dragging back right before letting go opens it again.
        rememberExpandedWidth(originalWidth.current || DEFAULT_SIDEBAR_WIDTH)
        setSidebarWidth(0)
      } else if (newWidth < minSidebarWidth) {
        setSidebarWidth(minSidebarWidth)
      } else if (newWidth > maxSidebarWidth) {
        setSidebarWidth(maxSidebarWidth)
      } else {
        setSidebarWidth(newWidth)
      }
    }
  }

  const handleMouseUp = () => {
    if (dragging.current === false) return
    dragging.current = false
    mouseStart.current = 0
  }

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  useEffect(() => {
    // `showNavigationMenu=false` is how an embedded Morta hides the nav, the
    // same collapsed state as the Hide sidebar button.
    const navigationMenu = searchParams.get('showNavigationMenu')
    const openSearchQueryParam = searchParams.get('openSearch')
    if (navigationMenu === 'false') setSidebarWidth(0)
    if (openSearchQueryParam === 'true') openSearch()
  }, [searchParams])

  const inHubContext = view.includes(HUB_BASE) || (view.includes(NO_PERMISSION) && !!project.publicId)
  // Search looks through the current hub's resources, so it needs a hub, and
  // the one in the address: on entering a hub the previous hub's id (or none)
  // stays loaded until the new one arrives. Matched the same way the project
  // provider matches it.
  const hubIdInPath = useMatch(`${HUB}/*`)?.params.projectId
  const canSearch = inHubContext && !!project.publicId && (!hubIdInPath || hubIdInPath === project.publicId)

  // Leaving a hub closes Search, and so does ?openSearch=true on a page with
  // no hub. Otherwise it stays open unseen and pops up in the next hub.
  useEffect(() => {
    if (!inHubContext && search.open) closeSearch()
  }, [inHubContext, search.open])

  const scopeParam = searchParams.get('scope') || 'all'
  // The scopes filter the home grid, so they always land on home: from a
  // settings or profile page, "All hubs" has to take you there, not just
  // rewrite the current page's query string.
  const setScope = (scope: string) => {
    const next = view === HOME ? new URLSearchParams(searchParams) : new URLSearchParams()
    next.set('scope', scope)
    next.delete('siteId')
    navigate({ pathname: HOME, search: `?${next.toString()}` })
  }

  // A site's search: the home grid narrowed to that site, with its search box
  // focused to type a hub's name.
  const searchSite = (siteId: string) =>
    navigate({ pathname: HOME, search: `?${new URLSearchParams({ scope: 'site', siteId, focus: 'search' })}` })

  const hubs = projects.filter((p) => !p.isDeleted && p.publicId !== COMMUNITY_PROJECT_ID)
  const hasArchived = projects.some((p) => p.isDeleted)
  const starred = hubs.filter((p) => isFavourite(p.publicId, user?.favourites)).sort(byName)
  const publicHubs = hubs.filter((p) => p.public)
  const { sites, unassigned } = groupHubsBySite(hubs, user?.favourites)
  // Most people's sites are all their own organisation's, which a badge on every one wouldn't tell them.
  // A site without organisation data (an empty key) isn't another organisation.
  const mixesOrganisations = new Set(sites.map((site) => site.organisation.key).filter(Boolean)).size > 1
  const allSitesCollapsed = sites.length > 0 && sites.every((site) => collapsedSites.has(site.key))
  // With sites, a new hub must go in one, so the leftovers section has no +.
  const unassignedLabel = sites.length > 0 ? `Other ${PROJECT}s` : `My ${PROJECT}s`

  const openCreateHub = (siteId?: string) => {
    setCreateHubSiteId(siteId)
    setCreateHubModal(true)
  }
  // The organisation the user administers, falling back to the company typed
  // on their profile. The API only sends organisations for their admins.
  const organisationName = user?.adminOrganisations?.[0]?.name || user?.organisation

  const toggleStar = (projectId: string) => {
    if (!user) return
    toggleFavouriteHub({
      user,
      projectId,
      updateUser,
      request: (args) => api(args),
      onError: displayErrorMessage
    })
  }

  // One row shape for a hub wherever it appears, starred list or full list.
  const hubRow = (hub: (typeof hubs)[number], key: string) => {
    const fav = isFavourite(hub.publicId, user?.favourites)
    return (
      <div key={key} className="group relative">
        <Link to={generatePath(HUB, { projectId: hub.publicId })}>
          <div className={`${ROW} ${ROW_IDLE} pr-8`}>
            <span className={SIDEBAR_SLOT}>
              <HubBadge
                name={hub.name}
                colour={hub.primaryColour}
                textColour={hub.fontColour}
                size={SIDEBAR_ICON_SIZE}
              />
            </span>
            <Tooltip content={hub.name} showOnlyIfTruncated delay={300}>
              <span className="truncate min-w-0 flex-1 text-left font-medium">{hub.name}</span>
            </Tooltip>
          </div>
        </Link>
        <button
          type="button"
          aria-label={fav ? `Unstar ${hub.name}` : `Star ${hub.name}`}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            toggleStar(hub.publicId)
          }}
          className={`absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-md bg-transparent border-0 p-0 transition-opacity [&>svg]:w-[13px] [&>svg]:h-[13px] ${
            fav ? 'opacity-100 text-alert' : 'opacity-0 group-hover:opacity-100 text-faint hover:text-alert'
          }`}
        >
          {fav ? <FilledStar /> : <NonFilledStar />}
        </button>
      </div>
    )
  }

  // Closes a section the user can add to, like New table / New document at
  // the foot of a hub's resources.
  const newHubRow = (siteId?: string) => (
    <div className={`${ROW} ${SIDEBAR_ROW_MUTED}`} {...rowButtonProps(() => openCreateHub(siteId))}>
      <span className={SIDEBAR_ICON_SEMI}>
        <Plus />
      </span>
      <span className="truncate font-medium">New {PROJECT}</span>
    </div>
  )

  // New hub where the organisation only lets Site Admins create hubs: shown, so
  // it is clear why there is no way to add one, but it does nothing.
  const disabledNewHubRow = (message: string) => (
    <Tooltip content={message}>
      <div
        className={`${ROW} text-muted opacity-45 cursor-not-allowed`}
        aria-disabled="true"
        data-testid="sidebar-new-hub-disabled"
      >
        <span className={SIDEBAR_ICON_SEMI}>
          <Plus />
        </span>
        <span className="truncate font-medium">New {PROJECT}</span>
      </div>
    </Tooltip>
  )

  const surface = USE_HUB_COLOUR_SURFACE ? project.primaryColour : SURFACE
  const surfaceText = USE_HUB_COLOUR_SURFACE ? project.fontColour : undefined

  return (
    <div data-testid="sidebar">
      {/* The way back once collapsed. It floats over the page, so unlike the
          edge buttons it keeps a fill. Sits where the collapse button's row
          would be, level with the logo row's centre; page headers leave it
          SIDEBAR_RESTORE_CLEARANCE of room. */}
      {sidebarWidth === 0 && (
        <div className="fixed z-[2000]" style={{ top: SIDEBAR_RESTORE_INSET, left: SIDEBAR_RESTORE_INSET }}>
          <Tooltip content="Show sidebar">
            <Button
              internalType="outline"
              className={`${EDGE_BUTTON} bg-surface hover:bg-surface-sunken shadow-sm`}
              data-testid="sidebar-toggle"
              aria-label="Show sidebar"
              onClick={() => setSidebarWidth(expandedWidth.current)}
            >
              <PanelLeftLine />
            </Button>
          </Tooltip>
        </div>
      )}

      <div
        // No border while hidden: at zero width it would still draw a 1px line
        // down the screen's left edge and nudge the page right by a pixel.
        className={`flex flex-col h-full overflow-hidden select-none ${sidebarWidth > 0 ? 'border-r' : ''}`}
        style={{
          minWidth: sidebarWidth,
          maxWidth: sidebarWidth,
          backgroundColor: surface,
          color: surfaceText,
          borderColor: BORDER
        }}
      >
        {/* Brand */}
        <div className="flex items-center justify-between pr-4 shrink-0" style={{ height: SIDEBAR_TOP_ROW_HEIGHT }}>
          <Link to={HOME}>
            {/* The wordmark is one raster: bars, then "Morta". Measured at 18px
              tall, the bars end 16.7px in and the M starts 24.4px in — a 7.7px
              gap, near enough the rows' 8px icon-to-label gap that one offset
              aligns both. At 23.6px the M lands on the label column (48px) and
              the bars end on the icon column (40px). Re-measure if the logo's
              height or artwork changes. */}
            <div className="flex items-center pl-[23.6px]" style={{ height: SIDEBAR_TOP_ROW_HEIGHT }}>
              <img
                alt="Morta"
                className="h-[18px] w-auto object-contain"
                src={validateSourceUrl(`${APP_URL}/assets/images/morta.webp`)}
              />
            </div>
          </Link>

          <div className="flex items-center gap-1">
            {user && (
              <Tooltip content="What's new">
                <Button
                  internalType="outline"
                  className={EDGE_BUTTON}
                  data-testid="whats-new-button"
                  aria-label={hasUnreadWhatsNew ? "What's new, unread" : "What's new"}
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    setWhatsNewMenu(menuStateBeside(event.currentTarget, sidebarWidth))
                  }
                >
                  <WhatsNew />
                  {hasUnreadWhatsNew && (
                    <span
                      data-testid="whats-new-badge"
                      className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red"
                    />
                  )}
                </Button>
              </Tooltip>
            )}
            {/* Collapse, for reading a document full screen (on mobile
                especially) or giving a wide table the room. */}
            <Tooltip content="Hide sidebar">
              <Button
                internalType="outline"
                className={EDGE_BUTTON}
                data-testid="sidebar-collapse"
                aria-label="Hide sidebar"
                onClick={collapseSidebar}
              >
                <PanelLeftLine />
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="px-2 shrink-0">
          {/* Outside a hub (home, settings, profile) Search has nothing to
              search, so it is left out rather than offered and doing nothing. */}
          {canSearch && (
            <div
              className={`${ROW} ${ROW_IDLE}`}
              data-testid="sidebar-search"
              {...rowButtonProps(() => (search.open ? closeSearch() : openSearch()))}
            >
              <span className={ICON}>
                <SearchIcon />
              </span>
              <span className="truncate">Search</span>
            </div>
          )}

          {/* The way to the full grid sits at the top on every page, inside a
              hub too, so it is always in the same place. */}
          <div
            className={`${ROW} ${view === HOME && scopeParam === 'all' ? ROW_ACTIVE : ROW_IDLE}`}
            data-tour="hubs-link"
            {...rowButtonProps(() => setScope('all'))}
          >
            <span className={ICON}>
              <Projects />
            </span>
            <span className="truncate font-medium">All {PROJECT}s</span>
          </div>
        </div>

        {/* The rows above act on everything; the lists below are content. A rule
            keeps them from reading as one undifferentiated stack. */}
        <div className="mx-3 mt-2 mb-1 border-t shrink-0" style={{ borderColor: BORDER }} />

        {/* Middle: hub list at the top level, hub contents one level in */}
        <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-2 ${SIDEBAR_SCROLL_HIDDEN}`}>
          {!inHubContext && (
            <>
              {/* Starred first, and only when there is something starred —
                  an empty "Starred" row tells you nothing. */}
              {starred.length > 0 && (
                <>
                  <div className={SECTION_LABEL}>Starred {PROJECT}s</div>
                  {starred.map((hub) => hubRow(hub, `fav-${hub.publicId}`))}
                </>
              )}

              {/* Under a Sites label, as Starred hubs has one: each site is a row that folds away the
                  hubs listed below it. Where the list mixes organisations, each site's row names its
                  organisation in a small badge. */}
              {sites.length > 0 && (
                <div data-testid="sidebar-sites">
                  <SitesLabel
                    className={SECTION_LABEL}
                    allCollapsed={allSitesCollapsed}
                    onToggleAll={() => toggleAllSites(sites.map((s) => s.key))}
                  />
                  {sites.map((site, index) => {
                    const collapsed = collapsedSites.has(site.key)
                    return (
                      // Space between one site's hubs and the next site, none between a site and its
                      // own hubs, so each reads as one block.
                      <div key={site.key} className={index > 0 ? 'mt-2' : undefined} data-testid="sidebar-site-section">
                        <SiteHeading
                          label={site.label}
                          organisation={mixesOrganisations ? site.organisation.name : undefined}
                          collapsed={collapsed}
                          onToggle={() => toggleSite(site.key)}
                          onSearch={() => searchSite(site.key)}
                          allCollapsed={allSitesCollapsed}
                          onToggleAll={() => toggleAllSites(sites.map((s) => s.key))}
                        />
                        {!collapsed && (
                          <SiteHubs>
                            {site.hubs.map((hub) => hubRow(hub, `site-${hub.publicId}`))}
                            {creatableSiteIds.has(site.key) && newHubRow(site.key)}
                            {restrictedSiteIds.has(site.key) && disabledNewHubRow(HUB_CREATION_RESTRICTED_MESSAGE)}
                          </SiteHubs>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Hubs outside any site: My hubs for the many users with no
                  sites, Other hubs beside site sections. With no hubs at all it
                  still shows, holding New hub, so a new account has a way to
                  make its first. */}
              {(unassigned.length > 0 || sites.length === 0) && (
                <div data-testid="sidebar-unassigned-section">
                  <div className={SECTION_LABEL}>{unassignedLabel}</div>
                  {unassigned.map((hub) => hubRow(hub, hub.publicId))}
                  {sites.length === 0 && (canCreateHub ? newHubRow() : disabledNewHubRow(restrictionMessage))}
                </div>
              )}

              {/* Community gets a section rather than one stray row. Destination
                  first, the public hubs you are in next, and the secondary way
                  in last — the same shape as the resource list, where create
                  actions close the section. */}
              <div className={SECTION_LABEL}>Community</div>
              <div
                className={`${ROW} ${view === HOME && scopeParam === 'community' ? ROW_ACTIVE : ROW_IDLE}`}
                {...rowButtonProps(() => setScope('community'))}
              >
                <span className={ICON}>
                  <Community />
                </span>
                <span className="truncate font-medium">Community {capitaliseFirstLetter(PROJECT)}s</span>
              </div>
              {publicHubs.map((hub) => hubRow(hub, `public-${hub.publicId}`))}
              {/* Copy, not Template: it is drawn as hollow rectangles, so it
                  matches the outline weight of the icons around it. The
                  Template glyph also overflows its own 0 0 14 14 viewBox
                  (its path reaches ~15.1) and renders clipped. */}
              <div className={`${ROW} ${ROW_IDLE}`} {...rowButtonProps(() => setTemplatesModal(true))}>
                <span className={SIDEBAR_ICON_SEMI}>
                  <Copy />
                </span>
                <span className="truncate font-medium">Templates</span>
              </div>

              {/* Last, like a trash can: without a Hubs section it would
                  otherwise read as part of whichever site came before it. */}
              {hasArchived && (
                <div
                  className={`mt-3 ${ROW} ${view === HOME && scopeParam === 'archived' ? ROW_ACTIVE : ROW_IDLE}`}
                  {...rowButtonProps(() => setScope('archived'))}
                >
                  <span className={ICON}>
                    <Archive />
                  </span>
                  <span className="truncate font-medium">Archived</span>
                </div>
              )}
            </>
          )}

          {inHubContext && (
            <>
              {/* Going one level deeper replaces the list rather than appending
                  to it; All hubs above is the way back. */}
              <Folders />
            </>
          )}
        </div>

        {/* Account / utilities */}
        <div className="shrink-0 border-t px-2 py-2" style={{ borderColor: BORDER }}>
          {!user && (
            <Link to={SIGN_IN}>
              <div className={`${ROW} ${ROW_IDLE}`}>
                <span className={SIDEBAR_SLOT}>
                  <img
                    alt="Anonymous User"
                    className="object-cover rounded-full"
                    style={{ width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }}
                    src={validateSourceUrl(DEFAULT_AVATAR_PATH)}
                  />
                </span>
                <span className="truncate">Sign In</span>
              </div>
            </Link>
          )}

          {user && (
            <>
              {/* The user and their notifications share a line: the bell sits
                  at the row's end, centred on the picture. */}
              <div className="flex items-center gap-1">
                {/* Hidden while its menu is open: the button sits at the foot of the window, so the
                    tooltip shows above it, where the menu opens. */}
                <Tooltip content="Account and settings" visible={userMenu.open ? false : undefined}>
                  <div
                    className={twMerge(`${ROW} ${ROW_IDLE} min-w-0 flex-1`, organisationName && 'h-11')}
                    data-tour="user-profile-button"
                    data-cy="user-menu"
                    aria-haspopup="menu"
                    aria-expanded={userMenu.open}
                    {...rowButtonProps((event) => {
                      // Above the account button, which sits at the foot of the window, lined up
                      // with its left edge: there's no room below it.
                      const rect = event.currentTarget.getBoundingClientRect()
                      setUserMenu({
                        open: true,
                        top: 'auto',
                        left: `${rect.left}px`,
                        right: 'auto',
                        bottom: `${window.innerHeight - rect.top + 6}px`
                      })
                    })}
                  >
                    {/* With the organisation line this is laid out like the hub
                    header: picture right-aligned to the icon column (40px), text
                    on the label column (48px), picture as tall as the two lines.
                    A circle reads smaller than the hub's square badge, so it is
                    a few px larger and hangs left of the column instead. */}
                    {organisationName ? (
                      <img
                        alt={user?.name}
                        className="object-cover rounded-full shrink-0 max-w-none"
                        style={{
                          width: USER_AVATAR_SIZE,
                          height: USER_AVATAR_SIZE,
                          marginLeft: SIDEBAR_HERO_BADGE - USER_AVATAR_SIZE
                        }}
                        src={validateSourceUrl(user?.profilePicture)}
                      />
                    ) : (
                      <span className={SIDEBAR_SLOT}>
                        <img
                          alt={user?.name}
                          className="object-cover rounded-full"
                          style={{ width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }}
                          src={validateSourceUrl(user?.profilePicture)}
                        />
                      </span>
                    )}
                    <span className="flex flex-col min-w-0 flex-1">
                      <span
                        className={`truncate font-semibold text-body ${organisationName ? 'text-[12.5px] leading-4' : ''}`}
                      >
                        {user?.name || 'Me'}
                      </span>
                      {organisationName && (
                        <span className="truncate text-[11px] leading-3 font-normal text-muted">
                          {organisationName}
                        </span>
                      )}
                    </span>
                  </div>
                </Tooltip>

                <div className="mr-2">
                  <Tooltip content="Notifications">
                    <Button
                      internalType="outline"
                      className={EDGE_BUTTON}
                      data-testid="notifications-bell"
                      aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        setNotificationsMenu(menuStateBeside(event.currentTarget, sidebarWidth))
                      }
                    >
                      <BellLine />
                      {unreadCount > 0 && (
                        <span
                          data-testid="notifications-badge"
                          className="absolute -top-1.5 -right-1.5 bg-red text-on-solid text-[10px] rounded-full px-1 min-w-[16px] text-center leading-4"
                        >
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </>
          )}
        </div>

        {userMenu.open && (
          <UserMenu id="user-menu" menuState={userMenu} setMenuState={setUserMenu} width={userMenuWidth} />
        )}
        {notificationsMenu.open && (
          <NotificationsPanel
            id="notifications-panel"
            menuState={notificationsMenu}
            setMenuState={setNotificationsMenu}
            width={380}
          />
        )}
        {whatsNewMenu.open && (
          <WhatsNewPanel id="whats-new-panel" menuState={whatsNewMenu} setMenuState={setWhatsNewMenu} width={380} />
        )}
        {inviteUserModal && (
          <InviteDialog
            onClose={() => setInviteUserModal(false)}
            onDone={() => {
              setInviteUserModal(false)
              setSnackbarMessage({ status: 'success', message: `Users were invited to ${PROJECT}` })
            }}
          />
        )}
        {templatesModal && <Templates open={templatesModal} setOpen={setTemplatesModal} id="templates-modal" />}
        {createHubModal && (
          <CreateHubModal
            id="sidebar-create-hub-modal"
            open={createHubModal}
            setOpen={setCreateHubModal}
            defaultSiteId={createHubSiteId}
          />
        )}
        {search.open && canSearch && <Search />}

        <div
          className="absolute grow-0 pointer-events-auto right-0 w-0 z-[1] top-0 bottom-0"
          style={{ left: `${sidebarWidth}px` }}
        >
          <div
            data-testid="sidebar-resize-handle"
            className="h-full cursor-col-resize w-3 ml-[-6px]"
            onMouseDown={(event: React.MouseEvent) => {
              event.preventDefault()
              handleMouseDown(event)
            }}
          />
        </div>
      </div>
    </div>
  )
}

// A sidebar render error must degrade to a fallback (and report to Sentry)
// rather than take down every page in the hub — see issue #10063. The
// fallback is constrained to the sidebar slot so the main content stays
// usable.
const AppSidebar: React.FC = () => (
  <ErrorBoundary fallbackClassName="h-full w-[240px] min-w-[240px] px-2.5">
    <AppSidebarContent />
  </ErrorBoundary>
)

export default AppSidebar
